import { uniqBy } from 'lodash';
import { Brief, BriefSection } from '@fiverr-private/ai_brief';
import { BriefInitialValues, Rollouts, useBriefCreationContext } from '../../context/BriefCreationContext';
import { BriefClassificationResponse } from '../../api';
import { getAttachmentsSection } from './Attachments';
import { getBudgetAndTimelineSection } from './BudgetAndTimeline';
import { getIdealCandidateSection } from './IdealCandidate';
import { getScreeningQuestionsSection } from './ScreeningQuestions';
import {
  MANDATORY_GENERATED_SECTION_TITLES,
  getBudgetSection,
  getMetadataSection,
  getNeedTypeSection,
  getNeedTypeTagsSection,
  getTimelineSection,
} from '.';

export const getMandatorySections = (
  briefClassification: BriefClassificationResponse,
  rollouts: Partial<Rollouts['rollouts']>
) => {
  const isPersonBriefEnabled = !!rollouts?.inMustangsPersonBrief;

  const metadataSection = getMetadataSection(briefClassification);
  const timelineSection = getTimelineSection(briefClassification);
  const budgetSection = getBudgetSection(briefClassification);

  return [...metadataSection, ...(!isPersonBriefEnabled ? [timelineSection, budgetSection] : [])];
};

export const getCustomSections = (
  brief: Brief<any>,
  briefClassification: BriefClassificationResponse,
  rollouts: Partial<Rollouts['rollouts']>,
  briefInitialValues?: Partial<BriefInitialValues>,
  enableScreeningQuestions = false
): BriefSection[] => {
  const isPersonBriefEnabled = !!rollouts?.inMustangsPersonBrief;

  const mandatorySections = getMandatorySections(briefClassification, rollouts);
  const attachmentsSection = getAttachmentsSection();
  const budgetAndTimelineSection = getBudgetAndTimelineSection(briefClassification, briefInitialValues);

  return [
    ...mandatorySections,
    ...(isPersonBriefEnabled ? [getIdealCandidateSection(brief, briefClassification)] : []),
    ...(isPersonBriefEnabled && enableScreeningQuestions ? [getScreeningQuestionsSection(brief)] : []),
    ...(isPersonBriefEnabled ? [budgetAndTimelineSection] : []),
    attachmentsSection,
  ];
};

export const addCustomSections = ({
  brief,
  additionalData,
  rollouts,
  briefInitialValues,
  enableEnhancedUx = false,
  enableScreeningQuestions = false,
}: {
  brief: Brief<any>;
  additionalData?: BriefClassificationResponse;
  rollouts: Partial<Rollouts['rollouts']>;
  briefInitialValues?: Partial<BriefInitialValues>;
  enableEnhancedUx?: boolean;
  enableScreeningQuestions?: boolean;
}) => {
  if (!additionalData) {
    return;
  }

  const isPersonBriefEnabled = !!rollouts?.inMustangsPersonBrief;
  const customSections = getCustomSections(
    brief,
    additionalData,
    rollouts,
    briefInitialValues,
    enableScreeningQuestions
  );

  const filteredCustomSections = brief.sections.filter(
    (section) => ![...customSections.map((section) => section.title), 'Timelines', 'Budget'].includes(section.title)
  );

  // TODO: remove this once we update BE to return the mandatory flag
  const filteredCustomSectionsWithMandatory = filteredCustomSections.map((section) =>
    section.title && MANDATORY_GENERATED_SECTION_TITLES.includes(section.title)
      ? { ...section, mandatory: true }
      : section
  );

  brief.sections = uniqBy(
    [
      ...(isPersonBriefEnabled
        ? [getNeedTypeTagsSection(enableEnhancedUx), getNeedTypeSection(additionalData, briefInitialValues)]
        : []),
      ...filteredCustomSectionsWithMandatory,
      ...customSections,
    ],
    'id'
  );

  return brief;
};

export const useCustomSections = () => {
  const { briefClassification, rollouts, briefInitialValues, enableEnhancedUx, enableScreeningQuestions } =
    useBriefCreationContext();

  const callback = ({ brief, additionalData }: { brief: Brief; additionalData?: BriefClassificationResponse }) =>
    addCustomSections({
      brief,
      additionalData: additionalData || briefClassification,
      rollouts,
      briefInitialValues,
      enableEnhancedUx,
      enableScreeningQuestions,
    });

  return {
    addCustomSections: callback,
  };
};
